import type { PlatformConnection } from '@/models/platform/types';

export type ConnectionScopeOptions = { brandId?: number; useSandbox?: boolean };

/**
 * Narrow platform-store surfaces the pure helpers below depend on. Kept minimal so callers can
 * pass the real Pinia store or a lightweight stub (metric predicates receive a stub via context).
 */
export interface PlatformConnectionReader {
  isPlatformNeverConnected: (
    platform: string | PlatformConnection,
    options?: ConnectionScopeOptions,
  ) => boolean;
}

export interface AdsConnectionReader {
  isPlatformConnected: (
    platform: string | PlatformConnection,
    options?: ConnectionScopeOptions,
  ) => boolean;
}

export interface AdAccountReader {
  getAccount: (
    platform: string | PlatformConnection,
    options?: Pick<ConnectionScopeOptions, 'brandId'>,
  ) => unknown[] | undefined;
}

/**
 * Whether an ad platform was **ever** connected (has a connection record) for the scoped brand —
 * distinct from being connected right now. Drives reconnect-vs-connect messaging. A missing
 * `platform` reads as never-connected (`false`). Scope defaults to the current brand; pass
 * `options.brandId` for a specific brand.
 */
export function wasAdsPlatformEverConnected(
  platformStore: PlatformConnectionReader,
  platform: PlatformConnection | string | null | undefined,
  options?: ConnectionScopeOptions,
): boolean {
  return !!platform && !platformStore.isPlatformNeverConnected(platform, options);
}

/**
 * Whether an ad platform is connected **right now** for the scoped brand — distinct from
 * ever-connected and from having ad accounts loaded. A missing `platform` reads as not connected
 * (`false`) — the guard matters where the platform is looked up dynamically (e.g.
 * `AD_SOURCE_PLATFORM_CONNECTIONS[source]`, which can be undefined).
 */
export function isAdsPlatformConnected(
  platformStore: AdsConnectionReader,
  platform: PlatformConnection | string | null | undefined,
  options?: ConnectionScopeOptions,
): boolean {
  return !!platform && platformStore.isPlatformConnected(platform, options);
}

/**
 * Whether the scoped brand has **at least one** connected ad account for the platform — distinct
 * from the platform being connected or ever-connected. A missing `platform` reads as no accounts
 * (`false`). Null-safe source for the `accounts[platform][brandId].length > 0` check that was
 * re-implemented inline (twice without a guard on the platform key, which could throw when no
 * account had ever loaded).
 */
export function hasConnectedAdAccounts(
  platformStore: AdAccountReader,
  platform: PlatformConnection | string | null | undefined,
  options?: Pick<ConnectionScopeOptions, 'brandId'>,
): boolean {
  if (!platform) return false;
  const accounts = platformStore.getAccount(platform, options);
  return Array.isArray(accounts) && accounts.length > 0;
}
